import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CAROUSEL_SUB, CAROUSEL_TITLE_BOTTOM, CAROUSEL_TITLE_TOP, carouselFootnote, ebookPages } from '../data/poolContent';
const EASE = [0.23, 1, 0.32, 1] as const;
const NAV_BTN = 'h-11 w-11 shrink-0 items-center justify-center rounded-full border border-aqua/40 text-aqua transition-colors duration-150 ease-out hover:bg-aqua hover:text-abyss-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-aqua';
export function EbookCarouselSection() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const go = (next: number) => {
    const total = ebookPages.length;
    const target = (next + total) % total;
    setDirection(target > index || index === total - 1 && target === 0 ? 1 : -1);
    setIndex(target);
  };
  const current = ebookPages[index];
  return <section className="w-full border-y border-pool/20 bg-black py-14 sm:py-20" aria-labelledby="carousel-title" data-id="element-76" data-mp="2yo27gexd5mox">
      <div className="mx-auto max-w-6xl px-5" data-id="element-77" data-mp="2trbs5ttduv4k">
        <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-aqua" data-id="element-78" data-mp="37gx44k8rma33">
          Inside the guide
        </p>
        <h2 id="carousel-title" className="mt-3 text-center font-display text-[2.2rem] font-bold uppercase leading-none text-frost-50 sm:text-[3.2rem]" data-id="element-79" data-mp="32pnfnmyydcss">
          {CAROUSEL_TITLE_TOP}
          <span className="block text-pool-light" data-id="element-80" data-mp="3cg08s3ku2eaj">{CAROUSEL_TITLE_BOTTOM}</span>
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-center text-[14px] leading-snug text-body sm:text-[15px]" data-id="element-81" data-mp="3qfhqjyzporae">
          {CAROUSEL_SUB}
        </p>

        <div className="mt-10 flex items-center justify-center gap-3 sm:gap-6" data-id="element-82" data-mp="1w1qkf9glcpjp">
          <button type="button" onClick={() => go(index - 1)} className={`hidden sm:flex ${NAV_BTN}`} data-id="element-83" data-mp="1w3tjsk3mvv13">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-84" data-mp="1s2rfhouv0ss9" />
            <span className="sr-only" data-id="element-85" data-mp="3r79hujqxkelz">Previous page</span>
          </button>

          <div className="relative w-full max-w-[19rem] overflow-hidden rounded-xl border border-abyss-600 bg-abyss-900 shadow-card sm:max-w-[22rem]" data-id="element-86" data-mp="3w4rqe911crtg">
            <div className="aspect-[9/16] w-full" data-id="element-87" data-mp="9wrwugyulph6">
              <AnimatePresence initial={false} mode="popLayout" custom={direction} data-id="element-88" data-mp="9ymdhj336vfg">
                <motion.img key={current.image} src={current.image} alt={`Guide page preview: ${current.caption}`} custom={direction} initial={{
                opacity: 0,
                x: direction * 28
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: direction * -28
              }} transition={{
                duration: 0.25,
                ease: EASE
              }} className="absolute inset-0 h-full w-full object-cover" width={768} height={1376} data-id="element-89" data-mp="dxvb4z18zdga" />
              </AnimatePresence>
            </div>
          </div>

          <button type="button" onClick={() => go(index + 1)} className={`hidden sm:flex ${NAV_BTN}`} data-id="element-90" data-mp="2i4woyg1jkjxu">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-91" data-mp="2d5bscc9ugnoa" />
            <span className="sr-only" data-id="element-92" data-mp="286gymfac9epe">Next page</span>
          </button>
        </div>

        <p className="mt-5 text-center text-[12px] font-semibold uppercase tracking-[0.18em] text-aqua" aria-live="polite" data-id="element-93" data-mp="288tmta6owlaw">
          {current.caption}
          <span className="ml-3 font-medium tracking-[0.14em] text-muted" data-id="element-94" data-mp="30vxvbp4zrhrf">
            {index + 1} / {ebookPages.length}
          </span>
        </p>

        <div className="mt-4 flex items-center justify-center gap-3 sm:hidden" data-id="element-95" data-mp="2r5uw377a17kn">
          <button type="button" onClick={() => go(index - 1)} className={`flex ${NAV_BTN}`} data-id="element-96" data-mp="3okqnrbmen7ha">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-97" data-mp="3ju0hzxa2h2m2" />
            <span className="sr-only" data-id="element-98" data-mp="3js77g6wc1y8s">Previous page</span>
          </button>
          <button type="button" onClick={() => go(index + 1)} className={`flex ${NAV_BTN}`} data-id="element-99" data-mp="34afqcaefornc">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-100" data-mp="38f5k9tpj7w5m" />
            <span className="sr-only" data-id="element-101" data-mp="2uf4j0d5cumpm">Next page</span>
          </button>
        </div>

        <ul className="no-scrollbar mt-7 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 sm:flex-wrap sm:justify-center sm:overflow-visible" data-id="element-102" data-mp="2zc4ghzo52h54">
          {ebookPages.map((page, i) => <li key={page.image} className="shrink-0 snap-start" data-id="element-103" data-mp="3s1uu87luzh0p" data-mpi={i}>
              <button type="button" onClick={() => go(i)} aria-current={i === index} className={`block w-[4.25rem] overflow-hidden rounded-md border transition-[border-color,opacity] duration-150 ease-out sm:w-[5rem] ${i === index ? 'border-aqua opacity-100' : 'border-abyss-600 opacity-50 hover:opacity-100'}`} data-id="element-104" data-mp="3s3eee9amyc7r">
                <img src={page.image} alt="" className="aspect-[9/16] w-full object-cover" width={768} height={1376} loading="lazy" data-id="element-105" data-mp="1zbizjijvvpja" />
                <span className="sr-only" data-id="element-106" data-mp="243amlj3tzd4o">View {page.caption}</span>
              </button>
            </li>)}
        </ul>

        <div className="mx-auto mt-10 flex max-w-3xl flex-col items-center justify-center gap-1 text-center sm:flex-row sm:gap-4" data-id="element-107" data-mp="1gernnu6o8cp9">
          {carouselFootnote.map(line => <p key={line} className="font-display text-[1.3rem] font-semibold uppercase text-frost-50" data-id="element-108" data-mp="1pf686rbt9nrp">
              {line}
            </p>)}
        </div>
      </div>
    </section>;
}